import { SkyButton, SkyDialog, SkyText } from "@skypointcloud/skypoint-ui";
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import { useEffect, useState, type ReactNode } from "react";
import type { NotifyChannel, TestAccepted, TestStatusView } from "@shared/types/notifications";
import {
  TEST_STATUS_GIVE_UP_MS,
  TEST_STATUS_POLL_MS,
  TEST_STATUS_SLOW_POLL_MS,
} from "@/hooks/use-notifications";
import { ApiClientError } from "@/lib/api";
import { InlineErrorBar } from "@/lib/feedback/InlineErrorBar";
import { describeFailure, reportFailureShown, type DescribedFailure } from "@/lib/feedback/failure";
import { platformTestRetryingSentence } from "@/lib/notifications/sentences";

/**
 * The platform tests' one dialog (contract-notif R9, §4.5): the test email (#2158) and the L1
 * Teams channel's test (#2165). Send writes an outbox row (202 with its id) and the dialog reads
 * the result every 2 s, and says it inline, because the dialog is the control that remains (ux-s10
 * §3 rule 2).
 *
 * #2158: a delivery can end for good after the 90 s give-up line (a refused key is tried once
 * more after 90 s), so the status is still read every 10 s after that line while the dialog is
 * open, and a try that already failed is said with its code's words while it is tried again.
 * "Still waiting" is only ever said while the delivery is pending.
 *
 * Results are a `role=status` line; a refusal of the request is `role=alert` inside the dialog.
 */
type Phase =
  | { kind: "idle" }
  | { kind: "waiting"; eventId: string; tooLong: boolean }
  | { kind: "result"; text: string; alert: boolean }
  | { kind: "error"; failure: DescribedFailure };

export interface PlatformTestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** The `data-testid` root: `<root>-dialog`, `<root>-confirm`, `<root>-status`, `<root>-error`. */
  testIdRoot: string;
  intro: ReactNode;
  channel: NotifyChannel;
  send: UseMutationResult<TestAccepted, Error, void>;
  useStatus: (eventId: string | null, pollMs: number | false) => UseQueryResult<TestStatusView>;
  /** The result line once the status is anything but `waiting`. */
  resultSentence: (answer: TestStatusView) => string;
  /** The line after 90 s with no try failed yet. */
  waitingTooLong: string;
  /** Words for the vault and the sender in a failure sentence. */
  context?: { vault?: string | null; sender?: string | null };
  /** A refused request said in the alert line (a 429, a 409), or null for the error bar. */
  refusalSentence: (error: ApiClientError) => string | null;
  /** "The test email could not be queued." */
  queueFailure: string;
  /** "The test email's status did not load." */
  statusFailure: string;
  /** Read once a result is shown (the card's own read). */
  onResult?: () => void;
}

/** The line under Send while a test is out: a failed try, "Sending…" or the give-up line. */
function waitingLine(
  props: Pick<PlatformTestDialogProps, "channel" | "context" | "waitingTooLong">,
  tooLong: boolean,
  answer: TestStatusView | undefined,
): string {
  const retrying = answer
    ? platformTestRetryingSentence(props.channel, answer, props.context)
    : null;
  if (retrying) return retrying;
  return tooLong ? props.waitingTooLong : "Sending…";
}

function pollIntervalOf(phase: Phase): number | false {
  if (phase.kind !== "waiting") return false;
  return phase.tooLong ? TEST_STATUS_SLOW_POLL_MS : TEST_STATUS_POLL_MS;
}

/** The phase a refused or failed send moves to: a refusal's own sentence, else the failure. */
function phaseOfSendError(props: PlatformTestDialogProps, error: unknown): Phase {
  reportFailureShown(error);
  const refused = error instanceof ApiClientError ? props.refusalSentence(error) : null;
  if (refused) return { kind: "result", alert: true, text: refused };
  const failure = describeFailure(error, "Try again.");
  return {
    kind: "error",
    failure: { ...failure, message: `${props.queueFailure} ${failure.message}` },
  };
}

/** The one line under the button for the phase the dialog is in. */
function lineOf(
  props: PlatformTestDialogProps,
  phase: Phase,
  sending: boolean,
  answer: TestStatusView | undefined,
): string {
  if (phase.kind === "result") return phase.text;
  if (phase.kind === "waiting") return waitingLine(props, phase.tooLong, answer);
  return sending ? "Sending…" : "";
}

export function PlatformTestDialog(props: PlatformTestDialogProps) {
  const { open, onOpenChange, title, testIdRoot, intro, send, useStatus, onResult } = props;
  const [phase, setPhase] = useState<Phase>({ kind: "idle" });

  const waiting = phase.kind === "waiting" && !phase.tooLong;
  const status = useStatus(phase.kind === "waiting" ? phase.eventId : null, pollIntervalOf(phase));

  useEffect(() => {
    if (phase.kind !== "waiting" || phase.tooLong) return;
    const handle = setTimeout(
      () =>
        setPhase((current) =>
          current.kind === "waiting" ? { ...current, tooLong: true } : current,
        ),
      TEST_STATUS_GIVE_UP_MS,
    );
    return () => clearTimeout(handle);
  }, [phase]);

  // A result moves the card it was sent from: read the card once.
  useEffect(() => {
    if (phase.kind !== "result" || !onResult) return;
    onResult();
  }, [phase, onResult]);

  const answer =
    phase.kind === "waiting" && status.data?.eventId === phase.eventId ? status.data : undefined;
  if (phase.kind === "waiting" && answer && answer.status !== "waiting") {
    setPhase({ kind: "result", alert: false, text: props.resultSentence(answer) });
  }
  if (phase.kind === "waiting" && status.isError && !answer) {
    reportFailureShown(status.error);
    setPhase({ kind: "error", failure: describeFailure(status.error, props.statusFailure) });
  }

  const busy = send.isPending || waiting;

  function sendTest() {
    if (busy) return;
    setPhase({ kind: "idle" });
    send.mutate(undefined, {
      onSuccess: (accepted) =>
        setPhase({ kind: "waiting", eventId: accepted.eventId, tooLong: false }),
      onError: (error) => setPhase(phaseOfSendError(props, error)),
    });
  }

  function close(next: boolean) {
    if (!next) setPhase({ kind: "idle" });
    onOpenChange(next);
  }

  const line = lineOf(props, phase, send.isPending, answer);
  const alertLine = phase.kind === "result" && phase.alert;

  return (
    <SkyDialog
      open={open}
      onOpenChange={close}
      title={title}
      size="medium"
      testId={`${testIdRoot}-dialog`}
      secondaryAction={{ label: "Close", onClick: () => close(false) }}
    >
      <div className="flex flex-col gap-4">
        <SkyText as="p" size={300} block>
          {intro}
        </SkyText>
        <div>
          {/* Not `disabled` while the test is out, so the focus stays on it (R14); a second
              press while the line says "Sending…" is ignored. */}
          <SkyButton
            appearance="primary"
            aria-disabled={busy}
            testId={`${testIdRoot}-confirm`}
            onClick={sendTest}
          >
            Send
          </SkyButton>
        </div>
        {phase.kind === "error" ? (
          <InlineErrorBar error={phase.failure} testId={`${testIdRoot}-error`} />
        ) : null}
        {/* Two stable live regions (review r1 N5): a region whose role changes is not reliably
            announced, so a refusal is written into the alert region and every other line into
            the status region; both stay mounted. */}
        <div data-testid={`${testIdRoot}-status`}>
          <output className="block" data-testid={`${testIdRoot}-status-polite`}>
            {line && !alertLine ? <SkyText size={300}>{line}</SkyText> : null}
          </output>
          <div role="alert" data-testid={`${testIdRoot}-status-alert`}>
            {line && alertLine ? <SkyText size={300}>{line}</SkyText> : null}
          </div>
        </div>
      </div>
    </SkyDialog>
  );
}
